import { useData } from "../../../../../runtime/provider";
import { useMemo } from "react";
import type { ReactNode } from "react";
import { router } from "expo-router";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import { useAppBuyer } from "@components/Checkout/appBuyer";
import { SheetCheckout } from "@components/Checkout/SheetCheckout";
import { TicketsSheet } from "@components/Checkout/TicketsSheet";
import useAccessConfirmMusicUnlock from "@hooks/trpc/useAccessConfirmMusicUnlock";
import useAccessCreateMusicUnlock from "@hooks/trpc/useAccessCreateMusicUnlock";
import { refreshAccesses } from "@utils/stripe/enginePayment";
import { useLandingStore } from "store/useLandingStore";
import { markMusicAccessUnlocked } from "../../utils/markMusicAccessUnlocked";
import type { MusicUnlockPorts } from "./musicUnlock";

type SheetContent = Parameters<MusicUnlockPorts["openSheet"]>[0];

/** What a music unlock reaches in the app: the fan's sign-in, the core
 *  API, the access caches, the navigation params Details reads after a
 *  purchase, and the sheet stack over Details. */
export function useMusicUnlockPorts(): MusicUnlockPorts {
  const data = useData();
  const buyer = useAppBuyer();
  const sheets = useBottomSheetManager();
  const { mutateAsync: create } = useAccessCreateMusicUnlock();
  const { mutateAsync: confirm } = useAccessConfirmMusicUnlock();
  const userId = buyer.user?.uid;
  return useMemo<MusicUnlockPorts>(
    () => ({
      buyer,
      isCurrentUser: () =>
        data.isCurrent() && !!userId && data.auth.currentUser()?.uid === userId,
      create,
      release: (input) => data.api.accesses.releaseEngineCheckout.mutate(input),
      confirm,
      markUnlocked: (accessId) => markMusicAccessUnlocked(data, accessId),
      clearUnlockCode: () =>
        useLandingStore.setState({ unlockCode: undefined }),
      complete: (accessId) =>
        router.setParams({ paymentResult: "success", accessId }),
      refresh: () => {
        refreshAccesses(data).catch(() => undefined);
      },
      openSheet: (content) =>
        sheets.open(
          <TicketsSheet>
            {({ closeModal }) => (
              <InSheet content={content} close={closeModal} />
            )}
          </TicketsSheet>,
          { mode: "push" },
        ),
      Checkout: SheetCheckout,
    }),
    [data, buyer, userId, create, confirm, sheets],
  );
}

/** The unlock's sheet, on the ports as they are now: a fan signing in in
 *  the sheet is the one the unlock is for. */
function InSheet({
  content,
  close,
}: {
  content: SheetContent;
  close: () => void;
}): ReactNode {
  const ports = useMusicUnlockPorts();
  return content({ close, ports });
}
